import React from 'react';
import { Result, Button, Typography, Card } from 'antd';
import { BugOutlined, ReloadOutlined, HomeOutlined } from '@ant-design/icons';

const { Paragraph, Text } = Typography;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { 
      hasError: true,
      errorId: Date.now().toString(36) + Math.random().toString(36).substr(2)
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error for debugging
    console.error('🚨 Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Log to external service if available
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: error.toString(),
        fatal: false
      });
    }
  }

  handleReload = () => {
    // Clear error state and reload
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
    window.location.reload();
  };

  handleGoHome = () => {
    // Clear error state and navigate to home
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #FFF8F0 0%, #FFFFFF 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <Card 
            style={{ 
              maxWidth: '600px', 
              width: '100%',
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(255, 140, 0, 0.1)'
            }}
          >
            <Result
              status="error"
              icon={<BugOutlined style={{ color: '#ff7875' }} />}
              title={<span style={{ color: '#FF8C00' }}>Oops! Something went wrong</span>}
              subTitle={
                <div>
                  <Paragraph style={{ color: '#666', marginBottom: '16px' }}>
                    We encountered an unexpected error in your spiritual journey app. 
                    Don't worry - your data is safe and we can help you get back on track.
                  </Paragraph>
                  
                  {process.env.NODE_ENV === 'development' && this.state.error && (
                    <Card 
                      size="small" 
                      title="Debug Information" 
                      style={{ 
                        marginBottom: '16px',
                        backgroundColor: '#fff2f0',
                        border: '1px solid #ffccc7'
                      }}
                    >
                      <Text code style={{ fontSize: '12px', color: '#ff4d4f' }}>
                        Error ID: {this.state.errorId}
                      </Text>
                      <br />
                      <Text code style={{ fontSize: '12px', color: '#ff4d4f' }}>
                        {this.state.error.toString()}
                      </Text>
                      {this.state.errorInfo.componentStack && (
                        <pre style={{ 
                          fontSize: '10px', 
                          color: '#666', 
                          marginTop: '8px',
                          maxHeight: '100px',
                          overflow: 'auto',
                          backgroundColor: '#fafafa',
                          padding: '8px',
                          borderRadius: '4px'
                        }}>
                          {this.state.errorInfo.componentStack}
                        </pre>
                      )}
                    </Card>
                  )}
                </div>
              }
              extra={[
                <Button 
                  type="primary" 
                  key="reload"
                  icon={<ReloadOutlined />}
                  onClick={this.handleReload}
                  style={{
                    background: 'linear-gradient(135deg, #FF8C00 0%, #FF7F00 100%)',
                    borderColor: '#FF8C00',
                    marginRight: '8px'
                  }}
                >
                  Reload App
                </Button>,
                <Button 
                  key="home"
                  icon={<HomeOutlined />}
                  onClick={this.handleGoHome}
                  style={{
                    borderColor: '#FFB347',
                    color: '#FF8C00'
                  }}
                >
                  Go Home
                </Button>
              ]}
            />
            
            <div style={{ 
              textAlign: 'center', 
              marginTop: '20px',
              padding: '16px',
              backgroundColor: '#fff7e6',
              borderRadius: '8px'
            }}>
              <Text style={{ color: '#fa8c16', fontSize: '14px' }}>
                🙏 <strong>Your data is safe.</strong> This is just a temporary display issue.
              </Text>
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 